// 🔎 B242 (AI_112) — BUSCA AVANÇADA de pacientes (visão clínica IDENTIFICADA).
// Reproduz a "Busca Avançada" do sistema antigo: filtros (protocolo/situação/
// período/palavra-chave/hospital) → LISTA de pacientes clicável. Visão de GESTÃO
// (médico-only) — distinta da pesquisa pseudonimizada. PERÍODO refere-se à DATA
// DA CONSULTA onde o termo aparece (pedido de Leonardo): a palavra-chave é
// procurada nas EVOLUÇÕES (e no perfil clínico quando não há período).
import React, { useMemo, useState } from "react";
import { Search } from "lucide-react";
import { theme } from "../../../shared/styles/theme";
import { RegistroPesquisa } from "../types";
import { useEvolucao } from "../../evolucao/context/EvolucaoContext";
import { temConsultaComTermoNoPeriodo, EvolucaoBusca } from "../services/buscaConsultas";
import TabelaPacientesPesquisa, { LinhaPaciente } from "./TabelaPacientesPesquisa";

function norm(v: unknown): string {
  return String(v ?? "")
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .trim()
    .toLowerCase();
}

const GRUPOS: { valor: string; label: string }[] = [
  { valor: "figado", label: "Fígado" },
  { valor: "rim", label: "Rim" },
  { valor: "pancreas", label: "Pâncreas" },
  { valor: "ambulatorial", label: "Ambulatório" },
];
// Situação: inclui "pré total" (inscritos + não inscritos).
const SITUACOES: { valor: string; label: string }[] = [
  { valor: "pre_total", label: "Pré-Tx (total)" },
  { valor: "pre", label: "Pré-Tx — não inscrito" },
  { valor: "lista", label: "Pré-Tx — inscrito" },
  { valor: "pos", label: "Pós-Tx" },
];

const input: React.CSSProperties = {
  padding: "8px 10px",
  border: `1px solid ${theme.gray[300]}`,
  borderRadius: theme.borderRadius.md,
  fontSize: "13px",
  fontFamily: theme.fonts.sans,
  background: "white",
  color: theme.gray[800],
};
const labelMini: React.CSSProperties = {
  fontSize: "11px",
  fontWeight: 600,
  color: theme.gray[500],
  marginBottom: "4px",
  display: "block",
};

export default function BuscaAvancada({
  registros,
  pacientesPorId,
  onAbrir,
}: {
  registros: RegistroPesquisa[];
  pacientesPorId: Map<string, any>;
  onAbrir: (id: string) => void;
}) {
  const { evolucoes } = useEvolucao();

  const [grupo, setGrupo] = useState("");
  const [situacao, setSituacao] = useState("");
  const [periodoInicio, setPeriodoInicio] = useState("");
  const [periodoFim, setPeriodoFim] = useState("");
  const [palavra, setPalavra] = useState("");
  const [hospital, setHospital] = useState("");

  // Evoluções (consultas) agrupadas por paciente (para a busca por termo/período).
  const evosPorPac = useMemo(() => {
    const m = new Map<string, EvolucaoBusca[]>();
    for (const ev of evolucoes as any[]) {
      const pid = ev?.pacienteId;
      if (!pid) continue;
      // B323 Bloco 3 (AI_146): consulta cancelada não entra na busca/coorte.
      if (ev?.cancelado === true) continue;
      const arr = m.get(pid);
      if (arr) arr.push(ev);
      else m.set(pid, [ev]);
    }
    return m;
  }, [evolucoes]);

  // Junta registro (derivado) + paciente (identificado).
  const juncao = useMemo(
    () =>
      registros
        .map((r) => ({ r, p: pacientesPorId.get(r._pacienteId as string) }))
        .filter((x) => x.p),
    [registros, pacientesPorId]
  );

  const hospitais = useMemo(
    () =>
      Array.from(
        new Set(juncao.map((x) => (x.p.hospitalTransplante || "").trim()).filter(Boolean))
      ).sort((a, b) => a.localeCompare(b)),
    [juncao]
  );

  const linhas: LinhaPaciente[] = useMemo(() => {
    const termoNorm = norm(palavra);
    const semPeriodo = periodoInicio === "" && periodoFim === "";
    const usaBuscaTexto = palavra.trim() !== "" || !semPeriodo;
    return juncao
      .filter(({ r, p }) => {
        if (grupo && r.grupo !== grupo) return false;
        if (situacao === "pre_total") {
          if (r.statusTransplante === "pos") return false;
        } else if (situacao) {
          if (r.statusTransplante !== situacao) return false;
        }
        if (hospital && (p.hospitalTransplante || "").trim() !== hospital) return false;
        if (usaBuscaTexto) {
          const id = r._pacienteId as string;
          const evos = evosPorPac.get(id);
          // Termo aparece numa consulta DENTRO do período (período = data da consulta).
          const naConsulta = temConsultaComTermoNoPeriodo(evos, palavra, periodoInicio, periodoFim);
          // Sem período, o termo também vale no perfil clínico estruturado.
          const noPerfil =
            semPeriodo && termoNorm !== "" && norm(r._textoBusca).includes(termoNorm);
          if (!naConsulta && !noPerfil) return false;
        }
        return true;
      })
      .map(({ r, p }) => ({
        id: r._pacienteId as string,
        nome: p.nome || "(sem nome)",
        grupo: (r.grupo as string) || null,
        status: (r.statusTransplante as string) || null,
        rgct: p.inscricaoTx?.numeroRGCT || p.numeroRGCT || p.rgct || "",
        dataTx: p.inscricaoTx?.dataTransplante || p.dataTransplante || "",
        hospital: p.hospitalTransplante || "",
      }))
      .sort((a, b) => a.nome.localeCompare(b.nome));
  }, [juncao, evosPorPac, grupo, situacao, periodoInicio, periodoFim, palavra, hospital]);

  function limpar() {
    setGrupo("");
    setSituacao("");
    setPeriodoInicio("");
    setPeriodoFim("");
    setPalavra("");
    setHospital("");
  }

  return (
    <div style={{ fontFamily: theme.fonts.sans }}>
      <div
        style={{
          background: "white",
          border: `1px solid ${theme.gray[200]}`,
          borderRadius: theme.borderRadius.lg,
          padding: "16px",
          marginBottom: "16px",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "12px" }}>
          <Search size={16} color={theme.gray[600]} />
          <strong style={{ fontSize: "14px", color: theme.gray[700] }}>
            Busca Avançada de Pacientes
          </strong>
          <span style={{ fontSize: "11px", color: theme.gray[400] }}>
            visão clínica identificada — uso de gestão
          </span>
        </div>

        <div style={{ display: "flex", flexWrap: "wrap", gap: "18px", alignItems: "flex-end" }}>
          <div>
            <label style={labelMini}>Protocolo</label>
            <select value={grupo} onChange={(e) => setGrupo(e.target.value)} style={input}>
              <option value="">Todos</option>
              {GRUPOS.map((g) => (
                <option key={g.valor} value={g.valor}>
                  {g.label}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label style={labelMini}>Situação</label>
            <select value={situacao} onChange={(e) => setSituacao(e.target.value)} style={input}>
              <option value="">Todas</option>
              {SITUACOES.map((s) => (
                <option key={s.valor} value={s.valor}>
                  {s.label}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label style={labelMini}>Período (data da consulta)</label>
            <div style={{ display: "flex", gap: "6px", alignItems: "center" }}>
              <input
                type="date"
                value={periodoInicio}
                onChange={(e) => setPeriodoInicio(e.target.value)}
                style={{ ...input, width: "150px" }}
              />
              <span style={{ color: theme.gray[400] }}>a</span>
              <input
                type="date"
                value={periodoFim}
                onChange={(e) => setPeriodoFim(e.target.value)}
                style={{ ...input, width: "150px" }}
              />
            </div>
          </div>
          <div style={{ flex: "1 1 240px", minWidth: "220px", marginRight: "8px" }}>
            <label style={labelMini}>Palavra-chave (consultas + perfil clínico)</label>
            <input
              type="text"
              value={palavra}
              onChange={(e) => setPalavra(e.target.value)}
              placeholder="ex.: hepatocarcinoma, colangite, varizes…"
              style={{ ...input, width: "100%" }}
            />
          </div>
          <div>
            <label style={labelMini}>Hospital de Transplante</label>
            <select value={hospital} onChange={(e) => setHospital(e.target.value)} style={input}>
              <option value="">Todos</option>
              {hospitais.map((h) => (
                <option key={h} value={h}>
                  {h}
                </option>
              ))}
            </select>
          </div>
          <button
            onClick={limpar}
            style={{
              padding: "8px 14px",
              background: "white",
              color: theme.gray[600],
              border: `1px solid ${theme.gray[300]}`,
              borderRadius: theme.borderRadius.md,
              fontSize: "13px",
              fontWeight: 600,
              cursor: "pointer",
            }}
          >
            Limpar
          </button>
        </div>

        <p style={{ fontSize: "11px", color: theme.gray[400], margin: "12px 0 0" }}>
          A palavra-chave é procurada no texto das <strong>consultas</strong> (queixa, história,
          conduta…) e o <strong>período</strong> filtra pela data da consulta onde ela aparece. Sem
          período, a palavra também vale no perfil clínico estruturado.
        </p>
      </div>

      <div
        style={{
          background: "white",
          border: `1px solid ${theme.gray[200]}`,
          borderRadius: theme.borderRadius.lg,
          padding: "16px",
        }}
      >
        <TabelaPacientesPesquisa
          linhas={linhas}
          onAbrir={onAbrir}
          colunas={["grupo", "status", "rgct", "dataTx", "hospital"]}
        />
      </div>
    </div>
  );
}
